import classNames from 'classnames';
import Selectro from 'components/common/Selectro';
import { ApplicationView as view } from '@aha-app/mvc';
import React from 'react';

/** @typedef {import('../../framework/models/ProjectTree').ProjectTree} ProjectTree */

/**
 * @typedef ProjectSelectProps
 * @prop {ProjectTree} projectTree
 * @prop {Parameters<ProjectTree['toSelectroOptions']>[0]=} filter
 * @prop {Aha.Project} selectedProject
 * @prop {((project: Aha.Project) => void)} onSelected
 * @prop {boolean=} showTooltip
 * @prop {Parameters<typeof classNames>[0]=} className
 * @prop {Parameters<typeof classNames>[0]=} selectroClasses
 * @prop {string=} nil
 */

/**
 * This is an implementation of the Aha! project selector backed by {@link ProjectTree}
 * making it suitable for use with the MVC framework:
 *
 * @example
 * controller:
 *   this.state.projectTree = await ProjectTree.load();
 * view:
 *   const { projectTree, project } = controller.state;
 *   return <ProjectSelect
 *            projectTree={projectTree}
 *            selectedProject={project}
 *            onSelected={controller.actionSetProject}
 *          />
 *
 * @type {React.FC<ProjectSelectProps>}
 */
const ProjectSelect = ({
  projectTree,
  filter,
  selectedProject,
  onSelected,
  showTooltip,
  className,
  selectroClasses,
  nil,
}) => {
  const selectedElements = selectedProject
    ? [[selectedProject.name, selectedProject.id]]
    : [];

  const tooltipProps =
    showTooltip && selectedProject
      ? {
          title: selectedProject.name,
          'data-title': selectedProject.name,
        }
      : {};

  return (
    <div className={classNames(className)} {...tooltipProps}>
      <Selectro
        placeholder='Select a workspace'
        selectedElements={selectedElements}
        fetchOptions={projectTree.toSelectroOptions(filter)}
        nil={<span className='text-muted'>{nil || 'Workspace…'}</span>}
        multiple={false}
        headersClickable={false}
        select={item => {
          onSelected(projectTree.findById(item.value));
        }}
        classes={selectroClasses || []}
      />
    </div>
  );
};

export default view(ProjectSelect);
